import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { creditsAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Sun, Moon, LogOut, User, ChevronDown, ChevronLeft, ChevronRight,
  LayoutDashboard, Headphones, Shield, Building, Users, Clock,
  AlertTriangle, DollarSign, Settings,
  Receipt, CreditCard, Menu, Languages, Coins, Share2, FileText, Key, FileStack, Lock, Loader2,
  ClipboardList, UserCheck, Phone, Brain, BarChart3, LayoutGrid, CalendarClock, Target, FileText as BlogIcon
} from 'lucide-react';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';

// Grouped admin navigation with collapsible sections
const ADMIN_NAV_SECTIONS = [
  {
    id: 'core',
    label: 'Operations',
    items: [
      { label: 'Overview', path: '/admin', icon: LayoutDashboard },
      { label: 'Organizations', path: '/admin/orgs', icon: Building },
      { label: 'Clients', path: '/admin/clients', icon: Users },
      { label: 'Interpreters', path: '/admin/interpreters', icon: Headphones },
      { label: 'Sessions', path: '/admin/sessions', icon: Clock },
      { label: 'Leads', path: '/admin/leads', icon: Target },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    items: [
      { label: 'Languages', path: '/admin/languages', icon: Languages },
      { label: 'Lang Requests', path: '/admin/language-requests', icon: FileStack },
      { label: 'Call Settings', path: '/admin/call-settings', icon: Phone },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    items: [
      { label: 'Invoices', path: '/admin/invoices', icon: Receipt },
      { label: 'Fee Schedules', path: '/admin/fee-schedules', icon: CreditCard },
      { label: 'Pay Rates', path: '/admin/pay-rates', icon: DollarSign },
      { label: 'Credits', path: '/admin/credits', icon: Coins },
      { label: 'Ledger', path: '/admin/ledger', icon: Receipt },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    items: [
      { label: 'Users', path: '/admin/users', icon: Key },
      { label: 'Compliance', path: '/admin/compliance', icon: Shield },
      { label: 'Blog', path: '/admin/blog', icon: BlogIcon },
      { label: 'Policies', path: '/admin/policies', icon: Settings },
      { label: 'Forms', path: '/admin/forms', icon: FileText },
      { label: 'SLA', path: '/admin/sla', icon: AlertTriangle },
      { label: 'Security', path: '/admin/security-profiles', icon: Shield },
      { label: 'AI Models', path: '/admin/ai-models', icon: Brain },
      { label: 'Audit Log', path: '/admin/audit-log', icon: ClipboardList },
      { label: 'Reports', path: '/reports', icon: BarChart3 },
      { label: 'Schedules', path: '/reports/schedules', icon: CalendarClock },
      { label: 'Dashboards', path: '/dashboards', icon: LayoutGrid },
    ],
  },
];

// Flat list for getNavItems and non-grouped rendering
const ADMIN_NAV = ADMIN_NAV_SECTIONS.flatMap(s => s.items);

const FINANCE_NAV = [
  { label: 'Dashboard', path: '/finance', icon: LayoutDashboard },
  { label: 'Invoices', path: '/admin/invoices', icon: Receipt },
  { label: 'Fee Schedules', path: '/admin/fee-schedules', icon: Receipt },
  { label: 'Pay Rates', path: '/admin/pay-rates', icon: CreditCard },
  { label: 'Organizations', path: '/admin/orgs', icon: Building },
  { label: 'Reports', path: '/reports', icon: BarChart3 },
  { label: 'Dashboards', path: '/dashboards', icon: LayoutGrid },
];

const HR_NAV = [
  { label: 'Dashboard', path: '/hr', icon: LayoutDashboard },
  { label: 'Interpreters', path: '/admin/interpreters', icon: UserCheck },
  { label: 'Languages', path: '/admin/languages', icon: Languages },
  { label: 'Lang Requests', path: '/admin/language-requests', icon: FileStack },
  { label: 'Reports', path: '/reports', icon: BarChart3 },
];

const CLIENT_NAV = [
  { label: 'Dashboard', path: '/client', icon: LayoutDashboard },
  { label: 'Reports', path: '/reports', icon: BarChart3 },
  { label: 'Dashboards', path: '/dashboards', icon: LayoutGrid },
];

const INTERPRETER_NAV = [
  { label: 'Dashboard', path: '/interpreter', icon: Headphones },
  { label: 'Reports', path: '/reports', icon: BarChart3 },
  { label: 'Dashboards', path: '/dashboards', icon: LayoutGrid },
];

const ORG_ADMIN_NAV = [
  { label: 'Org Settings', path: '/org-admin', icon: Building },
  { label: 'Admin Panel', path: '/admin', icon: Shield },
  { label: 'Reports', path: '/reports', icon: BarChart3 },
  { label: 'Dashboards', path: '/dashboards', icon: LayoutGrid },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [showCreditDetail, setShowCreditDetail] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [purchasing, setPurchasing] = useState(null);
  const [creditSettings, setCreditSettings] = useState({ base_rate_cents: 20, ai_credits_per_minute: 1, human_credits_per_minute: 3 });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false });
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav-collapsed-sections') || '{}'); } catch { return {}; }
  });

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      localStorage.setItem('nav-collapsed-sections', JSON.stringify(next));
      return next;
    });
  };

  const isAdmin = user?.role === 'admin';
  const isClient = user?.role === 'client';
  const isOrgAdmin = user?.role === 'org_admin';
  const userProfiles = user?.security_profiles || [];
  const isFinance = userProfiles.includes('Finance');
  const isHR = userProfiles.includes('Human Resources');
  const showCredits = isClient || isOrgAdmin;

  const getNavItems = () => {
    if (isAdmin) return ADMIN_NAV;
    if (user?.role === 'interpreter') return INTERPRETER_NAV;
    if (isOrgAdmin) return ORG_ADMIN_NAV;
    if (isFinance && isHR) return [...FINANCE_NAV, ...HR_NAV.filter(n => n.path !== '/hr')];
    if (isFinance) return FINANCE_NAV;
    if (isHR) return HR_NAV;
    return CLIENT_NAV;
  };
  const navItems = getNavItems();

  const loadBalance = useCallback(async () => {
    if (!showCredits) return;
    try {
      const [balRes, setRes] = await Promise.all([creditsAPI.getBalance(), creditsAPI.getSettings()]);
      setCreditBalance(balRes.data.balance);
      setCreditSettings(setRes.data);
    } catch (e) { /* ignore */ }
  }, [showCredits]);

  useEffect(() => { loadBalance(); }, [loadBalance]);

  const openBuyCredits = async () => {
    try {
      const res = await creditsAPI.listBundles();
      setBundles(res.data);
    } catch (e) { console.error(e); }
    setShowBuyCredits(true);
  };

  const handlePurchase = async (bundleId) => {
    setPurchasing(bundleId);
    try {
      const originUrl = window.location.origin;
      const res = await creditsAPI.createCheckout({ bundle_id: bundleId, origin_url: originUrl });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      toast.error('Failed to start checkout');
    }
    setPurchasing(null);
  };

  const isActive = (path) => {
    if (path === '/admin' && location.pathname === '/admin') return true;
    if (path !== '/admin' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-shell">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-60'} border-r bg-card/50 flex-shrink-0 hidden md:flex flex-col transition-all duration-200 sticky top-0 h-screen`}>
        <div className={`h-14 border-b flex items-center ${collapsed ? 'justify-center px-2' : 'px-4'} gap-2.5`}>
          <img
            src={collapsed ? (theme === 'dark' ? '/icon-white.png' : '/icon-coloured.png') : (theme === 'dark' ? '/logo-white.png' : '/logo-coloured.png')}
            alt="Interpreter IO"
            className={`${collapsed ? 'h-7 w-7 object-contain' : 'h-7'} cursor-pointer`}
            onClick={() => navigate(navItems[0]?.path || '/')}
          />
        </div>
        <nav className="flex-1 py-3 px-2 overflow-y-auto">
          {isAdmin ? (
            ADMIN_NAV_SECTIONS.map(section => {
              const isOpen = !collapsedSections[section.id];
              const sectionHasActive = section.items.some(item => isActive(item.path));
              return (
                <div key={section.id} className="mb-1">
                  {!collapsed ? (
                    <button
                      onClick={() => toggleSection(section.id)}
                      className={`w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-md transition-colors ${
                        sectionHasActive && !isOpen ? 'text-[hsl(var(--brand))]' : 'text-muted-foreground/60 hover:text-muted-foreground'
                      }`}
                      data-testid={`nav-section-${section.id}`}
                    >
                      <span>{section.label}</span>
                      <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                    </button>
                  ) : (
                    <div className="h-px bg-border mx-2 my-2" />
                  )}
                  {(isOpen || collapsed) && (
                    <div className="space-y-0.5">
                      {section.items.map(item => (
                        <button key={item.path} onClick={() => navigate(item.path)}
                          className={`w-full flex items-center gap-3 rounded-md text-sm transition-colors ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'} ${
                            isActive(item.path) ? 'bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] font-medium' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                          }`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                          title={collapsed ? item.label : undefined}
                        >
                          <item.icon className="w-4 h-4 flex-shrink-0" />
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="space-y-0.5">
              {navItems.map(item => (
                <button key={item.path} onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 rounded-md text-sm transition-colors ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'} ${
                    isActive(item.path) ? 'bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] font-medium' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </button>
              ))}
            </div>
          )}
        </nav>
        <div className="border-t p-2 text-center">
          <button onClick={() => setCollapsed(!collapsed)} className="w-full flex items-center justify-center p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors" data-testid="collapse-sidebar">
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
          {!collapsed && <p className="text-[10px] text-muted-foreground mt-1">&copy; 2026 Interpreter IO LLC</p>}
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-card border-r flex flex-col z-10">
            <div className="h-14 border-b flex items-center px-4 gap-2.5">
              <img src={theme === 'dark' ? '/logo-white.png' : '/logo-coloured.png'} alt="Interpreter IO" className="h-7" />
            </div>
            <nav className="flex-1 py-3 px-2">
              {isAdmin ? (
                ADMIN_NAV_SECTIONS.map(section => {
                  const isOpen = !collapsedSections[section.id];
                  return (
                    <div key={section.id} className="mb-1">
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60 hover:text-muted-foreground rounded-md transition-colors"
                      >
                        <span>{section.label}</span>
                        <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="space-y-0.5">
                          {section.items.map(item => (
                            <button key={item.path} onClick={() => { navigate(item.path); setMobileOpen(false); }}
                              className={`w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                                isActive(item.path) ? 'bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] font-medium' : 'text-muted-foreground hover:bg-accent'
                              }`}>
                              <item.icon className="w-4 h-4" /><span>{item.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="space-y-0.5">
                  {navItems.map(item => (
                    <button key={item.path} onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      className={`w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                        isActive(item.path) ? 'bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] font-medium' : 'text-muted-foreground hover:bg-accent'
                      }`}>
                      <item.icon className="w-4 h-4" /><span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </nav>
            <p className="text-[10px] text-muted-foreground text-center pb-3">&copy; 2026 Interpreter IO LLC</p>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40 flex items-center justify-between px-4 md:px-6">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="hidden md:block" />
          <div className="flex items-center gap-2">
            {/* Credit Balance & Buy Credits (client & org_admin only) */}
            {showCredits && creditBalance !== null && (
              <div className="flex items-center gap-1.5 mr-1">
                <Badge variant="outline" className="font-mono text-xs gap-1 px-2 py-1 cursor-pointer hover:bg-accent" onClick={() => setShowCreditDetail(true)} data-testid="credit-balance-badge">
                  <Coins className="w-3 h-3" />{creditBalance}
                </Badge>
                <Button variant="outline" size="sm" onClick={openBuyCredits} className="text-xs h-7 px-2 gap-1" data-testid="buy-credits-button">
                  <DollarSign className="w-3 h-3" />Buy Credits
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate('/referral')} title="Referrals" data-testid="header-referral-button">
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            )}
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="shell-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 rounded-full bg-[hsl(var(--brand))]/15 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-[hsl(var(--brand))]" />
                  </div>
                  <span className="hidden sm:block text-sm">{user?.name}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize mt-0.5">{user?.role?.replace('_', ' ')}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowPasswordDialog(true)} data-testid="change-password-menu">
                  <Key className="w-4 h-4 mr-2" />Change Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  try { const res = await authAPI.mfaStatus(); setMfaStatus(res.data); } catch {}
                  setMfaSetupData(null); setMfaCode('');
                  setShowMfaDialog(true);
                }} data-testid="mfa-settings-menu">
                  <Lock className="w-4 h-4 mr-2" />MFA Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>

      {/* Credit Detail Dialog (Client / Org Admin) */}
      <Dialog open={showCreditDetail} onOpenChange={setShowCreditDetail}>
        <DialogContent className="max-w-md" data-testid="credit-detail-dialog">
          <DialogHeader>
            <DialogTitle>Credit Balance</DialogTitle>
            <DialogDescription>Your organization's credit overview</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border p-5 text-center">
              <p className="text-5xl font-bold font-mono">{creditBalance ?? 0}</p>
              <p className="text-sm text-muted-foreground mt-1">Available Credits</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2 border-b text-sm">
                <span className="text-muted-foreground">Free Credits</span>
                <span className="font-mono">--</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b text-sm">
                <span className="text-muted-foreground">Monthly Subscription Credits</span>
                <span className="font-mono">--</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b text-sm">
                <span className="text-muted-foreground">Top-up Credits</span>
                <span className="font-mono">--</span>
              </div>
            </div>
            {/* Usage Rates */}
            <div className="rounded-md bg-secondary/50 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground">Credit Usage Rates</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Interpreter Services</span>
                <span className="font-mono text-xs">{creditSettings.ai_credits_per_minute} credit/min (~${((creditSettings.base_rate_cents * creditSettings.ai_credits_per_minute) / 100).toFixed(2)}/min)</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Human Interpreter</span>
                <span className="font-mono text-xs">{creditSettings.human_credits_per_minute} credits/min (~${((creditSettings.base_rate_cents * creditSettings.human_credits_per_minute) / 100).toFixed(2)}/min)</span>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 text-sm" data-testid="manage-subscription-btn" onClick={() => { setShowCreditDetail(false); toast.info('Subscription management coming soon'); }}>
                Manage Your Subscription
              </Button>
              <Button className="flex-1 text-sm bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="buy-more-credits-btn"
                onClick={() => { setShowCreditDetail(false); openBuyCredits(); }}>
                Buy More Credits
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buy Credits Dialog */}
      <Dialog open={showBuyCredits} onOpenChange={setShowBuyCredits}>
        <DialogContent className="max-w-md" data-testid="buy-credits-dialog">
          <DialogHeader>
            <DialogTitle>Buy Credits</DialogTitle>
            <DialogDescription>Purchase credit bundles for your organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-md bg-secondary/50 p-2.5 text-xs text-muted-foreground" data-testid="credit-rate-info">
              <span className="font-semibold">1 credit = ~${(creditSettings.base_rate_cents / 100).toFixed(2)}</span> &middot; AI: {creditSettings.ai_credits_per_minute} credit/min &middot; Human: {creditSettings.human_credits_per_minute} credits/min
            </div>
            {bundles.map(b => {
              const hasDiscount = b.discount_percent > 0;
              const effectivePrice = hasDiscount ? b.price_cents * (1 - b.discount_percent / 100) : b.price_cents;
              return (
                <div key={b.id} className="flex items-center justify-between rounded-lg border p-4 hover:bg-accent/50 transition-colors" data-testid={`buy-bundle-${b.id}`}>
                  <div>
                    <p className="font-semibold">{b.name}</p>
                    <p className="text-sm text-muted-foreground">{b.credits} credits</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      {hasDiscount && <p className="text-xs line-through text-muted-foreground">${(b.price_cents / 100).toFixed(2)}</p>}
                      <p className="font-mono font-bold">${(effectivePrice / 100).toFixed(2)}</p>
                      {hasDiscount && <Badge className="text-[10px] bg-green-500/10 text-green-600 border-green-500/20">{b.discount_percent}% off</Badge>}
                    </div>
                    <Button size="sm" onClick={() => handlePurchase(b.id)} disabled={purchasing === b.id}
                      className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid={`purchase-${b.id}`}>
                      {purchasing === b.id ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Buy'}
                    </Button>
                  </div>
                </div>
              );
            })}
            {bundles.length === 0 && <p className="text-center text-muted-foreground text-sm py-4">No credit bundles available</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(v) => { setShowPasswordDialog(v); if (!v) setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Update your account password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input type="password" value={passwordForm.current_password} onChange={e => setPasswordForm(p => ({ ...p, current_password: e.target.value }))} data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))} data-testid="new-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input type="password" value={passwordForm.confirm_password} onChange={e => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))} data-testid="confirm-password-input" />
            </div>
            <Button className="w-full bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
              data-testid="submit-password-change"
              onClick={async () => {
                if (passwordForm.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                setChangingPassword(true);
                try {
                  await authAPI.updatePassword({ current_password: passwordForm.current_password, new_password: passwordForm.new_password });
                  toast.success('Password updated successfully');
                  setShowPasswordDialog(false);
                  setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to update password');
                }
                setChangingPassword(false);
              }}>
              {changingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MFA Settings Dialog */}
      <Dialog open={showMfaDialog} onOpenChange={(v) => { setShowMfaDialog(v); if (!v) { setMfaSetupData(null); setMfaCode(''); } }}>
        <DialogContent className="max-w-sm" data-testid="mfa-settings-dialog">
          <DialogHeader>
            <DialogTitle>Multi-Factor Authentication</DialogTitle>
            <DialogDescription>Secure your account with TOTP</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {mfaStatus.mfa_enabled ? (
              <>
                <div className="rounded-lg border p-4 text-center bg-green-500/5 border-green-500/20">
                  <Lock className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="font-semibold text-green-600">MFA is Enabled</p>
                  <p className="text-xs text-muted-foreground mt-1">Your account is protected with TOTP</p>
                </div>
                <div className="space-y-2">
                  <Label>Enter current TOTP code to disable</Label>
                  <Input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-xl tracking-[0.4em] font-mono" data-testid="mfa-disable-code" />
                </div>
                <Button variant="destructive" className="w-full" disabled={mfaLoading || mfaCode.length !== 6}
                  data-testid="mfa-disable-btn"
                  onClick={async () => {
                    setMfaLoading(true);
                    try {
                      await authAPI.mfaDisable({ code: mfaCode });
                      toast.success('MFA disabled');
                      setMfaStatus({ mfa_enabled: false }); setMfaCode('');
                    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
                    setMfaLoading(false);
                  }}>
                  {mfaLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Disable MFA
                </Button>
              </>
            ) : mfaSetupData ? (
              <>
                <div className="text-center">
                  <p className="text-sm mb-3">Scan this QR code with your authenticator app:</p>
                  <img src={mfaSetupData.qr_code} alt="MFA QR Code" className="mx-auto w-48 h-48 rounded-lg border" data-testid="mfa-qr-code" />
                  <p className="text-[10px] text-muted-foreground mt-2 font-mono break-all">Secret: {mfaSetupData.secret}</p>
                </div>
                <div className="space-y-2">
                  <Label>Enter the 6-digit code from your app</Label>
                  <Input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-xl tracking-[0.4em] font-mono" data-testid="mfa-verify-code" />
                </div>
                <Button className="w-full bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" disabled={mfaLoading || mfaCode.length !== 6}
                  data-testid="mfa-verify-btn"
                  onClick={async () => {
                    setMfaLoading(true);
                    try {
                      await authAPI.mfaVerifySetup({ code: mfaCode });
                      toast.success('MFA enabled successfully!');
                      setMfaStatus({ mfa_enabled: true }); setMfaSetupData(null); setMfaCode('');
                    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
                    setMfaLoading(false);
                  }}>
                  {mfaLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Verify & Enable
                </Button>
              </>
            ) : (
              <>
                <div className="rounded-lg border p-4 text-center">
                  <Lock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="font-semibold">MFA is Not Enabled</p>
                  <p className="text-xs text-muted-foreground mt-1">Add an extra layer of security to your account</p>
                </div>
                <Button className="w-full bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" disabled={mfaLoading}
                  data-testid="mfa-setup-btn"
                  onClick={async () => {
                    setMfaLoading(true);
                    try {
                      const res = await authAPI.mfaSetup();
                      setMfaSetupData(res.data);
                    } catch (e) { toast.error('Failed to setup MFA'); }
                    setMfaLoading(false);
                  }}>
                  {mfaLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Set Up MFA
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
